import { motion, type Variants } from 'framer-motion';
import { Check, ArrowRight } from 'lucide-react';

const websiteBuilds = [
  {
    name: 'Basic',
    price: '£500',
    period: 'one-time',
    description: 'Single-page site for a sharp online presence.',
    features: [
      '1 page custom design',
      'Mobile responsive',
      'Contact form integration',
      '2 revision rounds',
      'Full source code',
    ],
    cta: 'Get Started',
    highlighted: false,
    planId: 'basic',
  },
  {
    name: 'Standard',
    price: '£1,500',
    period: 'one-time',
    description: 'Multi-page site for businesses ready to scale.',
    features: [
      'Up to 5 pages',
      'Mobile responsive',
      'Contact form integration',
      'CMS setup',
      '3 revision rounds',
      'Full source code',
    ],
    cta: 'Most Popular',
    highlighted: true,
    planId: 'standard',
  },
  {
    name: 'Premium',
    price: '£3,000',
    period: 'one-time',
    description: 'Comprehensive site with advanced features.',
    features: [
      'Up to 10 pages',
      'Custom animations',
      'CMS integration',
      'Blog setup',
      'Performance optimization',
      '5 revision rounds',
      'Full source code',
    ],
    cta: 'Go Premium',
    highlighted: false,
    planId: 'premium',
  },
];

const monthlyPlans = [
  {
    name: 'Starter',
    price: '£99',
    period: '/ month',
    description: 'Essential support to keep your site running smoothly.',
    features: [
      'Hosting & SSL',
      'Monthly backups',
      'Security updates',
      'Content changes (2/month)',
      'Email support',
      'Uptime monitoring',
    ],
    cta: 'Choose Starter',
    highlighted: false,
    planId: 'starter',
  },
  {
    name: 'Professional',
    price: '£249',
    period: '/ month',
    description: 'Growth-focused support with more flexibility.',
    features: [
      'Everything in Starter',
      'Priority hosting',
      'Weekly backups',
      'Content changes (10/month)',
      'Performance reports',
      'Minor design tweaks',
      'Phone & email support',
      'Quarterly review call',
    ],
    cta: 'Most Popular',
    highlighted: true,
    planId: 'professional',
  },
  {
    name: 'Enterprise',
    price: '£499',
    period: '/ month',
    description: 'Full-service partnership for ambitious businesses.',
    features: [
      'Everything in Professional',
      'Dedicated account manager',
      'Daily backups',
      'Unlimited content changes',
      'A/B testing setup',
      'Analytics dashboard',
      'New page builds (2/month)',
      '24/7 priority support',
    ],
    cta: 'Contact Us',
    highlighted: false,
    planId: 'enterprise',
  },
];

const container: Variants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.15 }
  }
};

const item: Variants = {
  hidden: { opacity: 0, y: 30 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5 } }
};

const Pricing = () => {
  return (
    <section id="pricing" className="py-24 bg-gray-50 dark:bg-dark">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-highlight font-medium text-sm uppercase tracking-wider">Pricing</span>
          <h2 className="text-4xl md:text-5xl font-display font-bold text-primary dark:text-white mt-3 mb-4">
            Transparent Pricing
          </h2>
          <p className="text-gray-600 dark:text-gray-400 max-w-2xl mx-auto text-lg">
            Build once. Support ongoing. You choose what fits.
          </p>
        </motion.div>

        {/* Website Builds - One Time */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="mb-16"
        >
          <h3 className="text-2xl font-bold text-primary dark:text-white text-center mb-2">
            Website Builds
          </h3>
          <p className="text-center text-gray-500 dark:text-gray-400 mb-8">One-time project fee</p>
          <motion.div
            variants={container}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-100px" }}
            className="grid md:grid-cols-3 gap-8"
          >
            {websiteBuilds.map((plan) => (
              <motion.div
                key={plan.name}
                variants={item}
                className={`relative rounded-2xl p-8 ${
                  plan.highlighted
                    ? 'bg-primary dark:bg-secondary shadow-xl scale-105 border-2 border-highlight'
                    : 'bg-white dark:bg-secondary/50 shadow-sm border border-gray-100 dark:border-white/10'
                }`}
              >
                {plan.highlighted && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 bg-highlight text-white text-sm font-semibold rounded-full">
                    Most Popular
                  </div>
                )}
                <div className="mb-6">
                  <h4 className={`text-xl font-bold mb-2 ${plan.highlighted ? 'text-white' : 'text-primary dark:text-white'}`}>
                    {plan.name}
                  </h4>
                  <p className={`text-sm ${plan.highlighted ? 'text-white/70' : 'text-gray-600 dark:text-gray-400'}`}>
                    {plan.description}
                  </p>
                </div>
                <div className="mb-6">
                  <span className={`text-4xl font-bold ${plan.highlighted ? 'text-white' : 'text-primary dark:text-white'}`}>
                    {plan.price}
                  </span>
                  <span className={`text-sm ml-2 ${plan.highlighted ? 'text-white/70' : 'text-gray-500 dark:text-gray-400'}`}>
                    {plan.period}
                  </span>
                </div>
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex items-center gap-3">
                      <Check size={18} className={plan.highlighted ? 'text-highlight' : 'text-green-500'} />
                      <span className={plan.highlighted ? 'text-white/90' : 'text-gray-700 dark:text-gray-300 text-sm'}>
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>
                <a
                  href={`/checkout?plan=${plan.planId}`}
                  className={`w-full py-3 px-6 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all ${
                    plan.highlighted
                      ? 'bg-highlight text-white hover:bg-highlight/90'
                      : 'bg-primary text-white hover:bg-secondary dark:bg-white dark:text-primary dark:hover:bg-gray-100'
                  }`}
                >
                  {plan.cta}
                  <ArrowRight size={16} />
                </a>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>

        {/* Monthly Services */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="mb-12"
        >
          <h3 className="text-2xl font-bold text-primary dark:text-white text-center mb-2">
            Monthly Services
          </h3>
          <p className="text-center text-gray-500 dark:text-gray-400 mb-8">Ongoing support &amp; maintenance</p>
        </motion.div>

        <motion.div
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-100px" }}
          className="grid md:grid-cols-3 gap-8"
        >
          {monthlyPlans.map((plan) => (
            <motion.div
              key={plan.name}
              variants={item}
              className={`relative rounded-2xl p-8 ${
                plan.highlighted
                  ? 'bg-primary dark:bg-secondary shadow-xl scale-105 border-2 border-highlight'
                  : 'bg-white dark:bg-secondary/50 shadow-sm border border-gray-100 dark:border-white/10'
              }`}
            >
              {plan.highlighted && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 bg-highlight text-white text-sm font-semibold rounded-full">
                  Most Popular
                </div>
              )}
              <div className="mb-6">
                <h3 className={`text-xl font-bold mb-2 ${plan.highlighted ? 'text-white' : 'text-primary dark:text-white'}`}>
                  {plan.name}
                </h3>
                <p className={`text-sm ${plan.highlighted ? 'text-white/70' : 'text-gray-600 dark:text-gray-400'}`}>
                  {plan.description}
                </p>
              </div>
              <div className="mb-6">
                <span className={`text-4xl font-bold ${plan.highlighted ? 'text-white' : 'text-primary dark:text-white'}`}>
                  {plan.price}
                </span>
                <span className={`text-sm ${plan.highlighted ? 'text-white/70' : 'text-gray-500 dark:text-gray-400'}`}>
                  {plan.period}
                </span>
              </div>
              <ul className="space-y-3 mb-8">
                {plan.features.map((feature) => (
                  <li key={feature} className="flex items-center gap-3">
                    <Check size={18} className={plan.highlighted ? 'text-highlight' : 'text-green-500'} />
                    <span className={plan.highlighted ? 'text-white/90' : 'text-gray-700 dark:text-gray-300 text-sm'}>
                      {feature}
                    </span>
                  </li>
                ))}
              </ul>
              <a
                href={`/checkout?plan=${plan.planId}`}
                className={`w-full py-3 px-6 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all ${
                  plan.highlighted
                    ? 'bg-highlight text-white hover:bg-highlight/90'
                    : 'bg-primary text-white hover:bg-secondary dark:bg-white dark:text-primary dark:hover:bg-gray-100'
                }`}
              >
                {plan.cta}
                <ArrowRight size={16} />
              </a>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Pricing;
